// The chat store's network: send, the live feed, stop, history (GRO-274).
//
// The feed is read with `fetch` rather than `EventSource`, because EventSource cannot send the
// Clerk bearer and the token must not go in a URL. The server sends a `ready` frame first; when it
// does not arrive within a few seconds something between here and Render is buffering the stream,
// and this transport switches to the long-poll route for the rest of the visit.

import {
  ApiError, fetchChatConversations, fetchChatEvents, fetchChatHistory, openChatStream, postChat, postChatSeen,
  postChatStop,
} from './client';
import type { TokenGetter } from './client';
import type { TurnViewBody } from './types';
import { endsFeed, parseSseBuffer, pollReadOutcome } from '../lib/chatFeed.ts';
import type { FeedEvent } from '../lib/chatFeed.ts';
import type { ChatTransport, ConversationSummary, FeedRead, HistoryReply, SendReply, ThreadAddress } from '../lib/chatStore.ts';

// How long the first frame may take before the stream counts as buffered.
const READY_TIMEOUT_MS = 6000;
// The long-poll route's wait per read.
const LONG_POLL_WAIT_SECONDS = 20;

interface TransportState {
  getToken: TokenGetter;
  pollOnly: boolean;
}

/** A frame's cursor contribution: one past its index, when it has one. */
function cursorAfter(event: FeedEvent, cursor: number | null): number | null {
  return typeof event.index === 'number' ? event.index + 1 : cursor;
}

/** One SSE read to its end. `buffered` when the ready frame never came in time. */
async function readSse(
  state: TransportState, address: ThreadAddress, startCursor: number | null, signal: AbortSignal,
  onEvent: (event: FeedEvent) => void,
): Promise<FeedRead & { buffered: boolean; full: boolean }> {
  let cursor = startCursor;
  let sawReady = false;
  let readyTimedOut = false;
  let sawIdle = false;

  // One controller for this read: the store's abort, the ready timeout and our own early return.
  const readAbort = new AbortController();
  const relayAbort = () => readAbort.abort();
  signal.addEventListener('abort', relayAbort);
  const readyTimer = setTimeout(() => {
    if (sawReady) return;
    readyTimedOut = true;
    readAbort.abort();
  }, READY_TIMEOUT_MS);
  try {
    const response = await openChatStream(state.getToken, address, startCursor, readAbort.signal);
    const reader = response.body?.getReader();
    if (!reader) return { cursor, ended: false, failed: true, buffered: false, full: false };
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      // The server closes after an idle (and its grace for a queued turn), or at its own limit.
      const { value, done } = await reader.read();
      if (done) return { cursor, ended: sawIdle, failed: false, buffered: false, full: false };
      buffer += decoder.decode(value, { stream: true });
      const parsed = parseSseBuffer(buffer);
      buffer = parsed.rest;
      for (const event of parsed.events) {
        if (event.type === 'ready') { sawReady = true; continue; }
        cursor = cursorAfter(event, cursor);
        if (event.type === 'idle') sawIdle = true;
        if (event.type === 'turn_started') sawIdle = false;
        onEvent(event);
        if (event.type === 'relay_error') return { cursor, ended: false, failed: true, buffered: false, full: false };

        // A parked card or an ended session: nothing more comes on this feed.
        if (endsFeed(event) && event.type !== 'idle') return { cursor, ended: true, failed: false, buffered: false, full: false };
      }
    }
  } catch (caught) {
    if (readyTimedOut) return { cursor, ended: false, failed: false, buffered: true, full: false };
    if (signal.aborted) return { cursor, ended: false, failed: false, buffered: false, full: false };
    const full = caught instanceof ApiError && caught.status === 503;
    return { cursor, ended: false, failed: !full, buffered: false, full };
  } finally {
    clearTimeout(readyTimer);
    signal.removeEventListener('abort', relayAbort);
    readAbort.abort();
  }
}

/** One long-poll read. */
async function readPoll(
  state: TransportState, address: ThreadAddress, startCursor: number | null, onEvent: (event: FeedEvent) => void,
): Promise<FeedRead> {
  try {
    const reply = await fetchChatEvents(state.getToken, address, startCursor, LONG_POLL_WAIT_SECONDS);
    for (const event of reply.events) onEvent(event);

    // A parked card or an ended session ends the read, as on the stream: polling again would loop.
    const outcome = pollReadOutcome(reply.events, reply.ended);
    return { cursor: reply.cursor, ended: outcome.ended, failed: outcome.failed };
  } catch {
    return { cursor: startCursor, ended: false, failed: true };
  }
}

async function readFeed(
  state: TransportState, address: ThreadAddress, cursor: number | null, signal: AbortSignal,
  onEvent: (event: FeedEvent) => void,
): Promise<FeedRead> {
  if (state.pollOnly) return readPoll(state, address, cursor, onEvent);
  const read = await readSse(state, address, cursor, signal, onEvent);
  if (read.buffered) {
    // Nothing came, not even `ready`: the stream is being buffered. Long-poll from here on.
    console.warn('scout chat stream looked buffered; switching to long-poll.');
    state.pollOnly = true;
    return readPoll(state, address, cursor, onEvent);
  }

  // The server's relay cap is full: long-poll this read only, and try the stream again next time.
  if (read.full) return readPoll(state, address, read.cursor, onEvent);

  return { cursor: read.cursor, ended: read.ended, failed: read.failed };
}

async function send(state: TransportState, address: ThreadAddress, message: string, view?: TurnViewBody): Promise<SendReply> {
  const reply = await postChat(state.getToken, view ? { message, ...address, view } : { message, ...address }) as { accepted?: boolean; available: boolean; reason?: string | null; stream_cursor?: number | null };
  return { accepted: Boolean(reply.accepted && reply.available), reason: reply.reason, stream_cursor: reply.stream_cursor };
}

async function stop(state: TransportState, address: ThreadAddress): Promise<boolean> {
  return (await postChatStop(state.getToken, address)).stopped;
}

async function history(state: TransportState, address: ThreadAddress): Promise<HistoryReply> {
  return fetchChatHistory(state.getToken, address);
}

async function conversations(state: TransportState): Promise<ConversationSummary[]> {
  return (await fetchChatConversations(state.getToken)).conversations;
}

async function seen(state: TransportState, address: ThreadAddress): Promise<void> {
  await postChatSeen(state.getToken, address);
}

/** The real transport, bound to the signed-in person's token getter. */
export function createChatTransport(getToken: TokenGetter): ChatTransport {
  const state: TransportState = { getToken, pollOnly: false };
  return {
    send: (address, message, view) => send(state, address, message, view),
    readFeed: (address, cursor, signal, onEvent) => readFeed(state, address, cursor, signal, onEvent),
    stop: address => stop(state, address),
    history: address => history(state, address),
    conversations: () => conversations(state),
    seen: address => seen(state, address),
  };
}
